import worldCountries, { type Country } from "world-countries";

export type CountryPresence = {
  name: string;
  latitude: number;
  longitude: number;
  region: string;
};

const normalizeCountryKey = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ");

const toPresence = (country: Country, displayName?: string): CountryPresence | null => {
  if (!Array.isArray(country.latlng) || country.latlng.length < 2) {
    return null;
  }

  return {
    name: displayName || country.name.common,
    latitude: country.latlng[0],
    longitude: country.latlng[1],
    region: country.region || "Other",
  };
};

const withDisplayName = (
  presence: CountryPresence | undefined,
  name: string,
): CountryPresence | undefined =>
  presence
    ? {
        ...presence,
        name,
      }
    : undefined;

const collectCountryVariants = (country: Country) => {
  const variants = new Set<string>([
    country.name.common,
    country.name.official,
    ...(country.altSpellings || []),
    ...Object.values(country.translations || {}).flatMap((translation) => [
      translation.common,
      translation.official,
    ]),
    ...Object.values(country.name.native || {}).flatMap((nativeName) => [
      nativeName.common,
      nativeName.official,
    ]),
  ]);

  return Array.from(variants).filter(Boolean);
};

const worldCountryCoordinates = worldCountries.reduce<Record<string, CountryPresence>>(
  (accumulator, country) => {
    const presence = toPresence(country);

    if (!presence) {
      return accumulator;
    }

    collectCountryVariants(country).forEach((variant) => {
      accumulator[normalizeCountryKey(variant)] = presence;
    });

    return accumulator;
  },
  {},
);

const aliasCoordinates: Record<string, CountryPresence> = {
  england: {
    name: "England",
    latitude: 52.3555,
    longitude: -1.1743,
    region: "Europe",
  },
  scotland: {
    name: "Scotland",
    latitude: 56.4907,
    longitude: -4.2026,
    region: "Europe",
  },
  wales: {
    name: "Wales",
    latitude: 52.1307,
    longitude: -3.7837,
    region: "Europe",
  },
  uk: worldCountryCoordinates["united kingdom"],
  usa: worldCountryCoordinates["united states"],
  "dr congo": worldCountryCoordinates["democratic republic of the congo"],
  "czech republic": withDisplayName(worldCountryCoordinates["czechia"], "Czech Republic"),
  turkey: withDisplayName(worldCountryCoordinates["turkiye"], "Turkey"),
  "holy see": withDisplayName(worldCountryCoordinates["vatican city"], "Holy See"),
};

const countryCoordinates = {
  ...worldCountryCoordinates,
  ...Object.fromEntries(Object.entries(aliasCoordinates).filter(([, value]) => Boolean(value))),
};

export const resolveCountryPresence = (countryName: string): CountryPresence | null => {
  const normalized = normalizeCountryKey(countryName);
  return countryCoordinates[normalized] || null;
};

export const countryPresenceForList = (countries: string[]) => {
  const seen = new Set<string>();

  return countries
    .map((country) => {
      const metadata = resolveCountryPresence(country);

      if (metadata) {
        return {
          ...metadata,
          sourceName: country,
          key: normalizeCountryKey(country),
          mapped: true,
        };
      }

      return {
        name: country,
        sourceName: country,
        key: normalizeCountryKey(country),
        mapped: false,
      };
    })
    .filter((country) => {
      if (seen.has(country.key)) {
        return false;
      }

      seen.add(country.key);
      return true;
    });
};
